import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { playChime } from "@/lib/chime";
import { cn } from "@/lib/utils";

import { fullName } from "../funnel/hooks";
import { useTaskActions } from "../funnel/taskActions";
import { taskNudges, type Nudge } from "../funnel/taskNudges";
import type { TaskCard } from "../types";

const SEEN_KEY = "oselya.nudges";

const loadSeen = () => {
  try {
    return new Set<string>(
      JSON.parse(sessionStorage.getItem(SEEN_KEY) ?? "[]"),
    );
  } catch {
    return new Set<string>();
  }
};

/**
 * Corner pop-ups for my tasks: soon, now, overdue. Each once per browser tab
 * session, with a soft bell, and buttons to finish or move the task right there.
 */
export const TaskNudges = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { identity } = useGetIdentity();
  const actions = useTaskActions();
  const seen = useRef(loadSeen());
  const { data: tasks = [] } = useGetList<TaskCard>(
    "task_cards",
    {
      filter: { "done_date@is": null, sales_id: identity?.id },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "due_date", order: "ASC" },
    },
    { enabled: identity?.id != null, refetchInterval: 60_000 },
  );

  useEffect(() => {
    const check = () => {
      const nudges = taskNudges(tasks, new Date(), seen.current).slice(0, 3);
      if (nudges.length === 0) return;
      nudges.forEach((nudge) => seen.current.add(nudge.key));
      try {
        sessionStorage.setItem(
          SEEN_KEY,
          JSON.stringify([...seen.current].slice(-300)),
        );
      } catch {
        // fine: worst case a pop-up repeats after reload
      }
      playChime("nudge");
      nudges.forEach((nudge) => show(nudge));
    };
    const show = ({ task, kind }: Nudge) =>
      toast.custom(
        (id) => (
          <div
            className={cn(
              "w-[340px] rounded-2xl border bg-popover p-4 text-popover-foreground shadow-lg",
              kind === "overdue" && "border-destructive/50",
            )}
          >
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
              {translate(`crm.tasks_page.nudge.${kind}`)}
            </div>
            <div className="mt-1 font-bold leading-tight">{task.text}</div>
            <div className="text-sm text-muted-foreground">
              {fullName(task)}
            </div>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground"
                onClick={async () => {
                  toast.dismiss(id);
                  await actions.done(task);
                }}
              >
                ✓ {translate("crm.tasks_page.action_done")}
              </button>
              <button
                type="button"
                className="rounded-lg border px-3 py-1.5 text-sm font-semibold"
                onClick={async () => {
                  toast.dismiss(id);
                  await actions.later(task, 15);
                }}
              >
                +15 {translate("crm.tasks_page.minutes")}
              </button>
              {task.deal_id ? (
                <button
                  type="button"
                  className="rounded-lg border px-3 py-1.5 text-sm font-semibold"
                  onClick={() => {
                    toast.dismiss(id);
                    navigate(`/funnel/${task.deal_id}`);
                  }}
                >
                  {translate("crm.tasks_page.open")}
                </button>
              ) : null}
            </div>
          </div>
        ),
        {
          position: "bottom-right",
          duration: kind === "overdue" ? 30_000 : 20_000,
        },
      );
    check();
    const timer = window.setInterval(check, 30_000);
    return () => window.clearInterval(timer);
  }, [tasks, translate, navigate, actions]);

  return null;
};
